import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playTickSound, playCorrectChime } from '../utils/soundEffects';

const {
  FiUpload, FiDownload, FiFileText, FiCheckCircle, FiAlertCircle,
  FiKey, FiPlus, FiTrash2, FiRefreshCw, FiSearch, FiCopy, FiCheck,
  FiShield, FiList, FiClock, FiLayers, FiHelpCircle, FiChevronDown, FiChevronUp
} = FiIcons;

const SAMPLE_CSV_ROWS = [
  { "Token Number": "EVENT-1001" },
  { "Token Number": "EVENT-1002" },
  { "Token Number": "EVENT-1003" },
  { "Token Number": "VIP-2026" },
  { "Token Number": "BOOTH-55" },
  { "Token Number": "TICKET-8899" },
  { "Token Number": "PASS-401" }
];

export default function TokenSerialManager({ settings, onUpdateSettings }) {
  // Mode tabs: 'upload' | 'generate' | 'quick_add' | 'paste'
  const [activeSubTab, setActiveSubTab] = useState('upload');
  
  // File upload state
  const [uploadMode, setUploadMode] = useState('append'); // 'append' | 'replace'
  const [parsedPreview, setParsedPreview] = useState(null); // { validTokens, skippedCount, totalFound }
  const [uploadStatus, setUploadStatus] = useState(null); // { type: 'success' | 'error', message: '' }
  const fileInputRef = useRef(null);

  // Generator state
  const [genPrefix, setGenPrefix] = useState('EVENT-');
  const [genStartIndex, setGenStartIndex] = useState(1);
  const [genDigits, setGenDigits] = useState(4);
  const [genCount, setGenCount] = useState(50);
  const [genStyle, setGenStyle] = useState('sequential'); // 'sequential' | 'alphanumeric'

  // Quick single add state
  const [singleToken, setSingleToken] = useState('');
  
  // Table search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'available' | 'used'
  const [copiedToken, setCopiedToken] = useState(null);
  const [showRawEditor, setShowRawEditor] = useState(false);
  const [rawTextValue, setRawTextValue] = useState((settings?.validSerialNumbers || []).join('\n'));

  const validTokens = settings?.validSerialNumbers || [];
  const usedTokens = settings?.usedSerialNumbers || [];

  const availableTokens = validTokens.filter(t => !usedTokens.includes(t));
  const redeemedTokens = validTokens.filter(t => usedTokens.includes(t));

  // --- 1. Download Sample Templates ---
  const handleDownloadSampleExcel = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(SAMPLE_CSV_ROWS);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Event Tokens");
      XLSX.writeFile(wb, "sample_tokens_template.xlsx");
      playTickSound(880, 0.05);
    } catch (e) {
      console.error("Excel template download error:", e);
      alert("Failed to create Excel template: " + e.message);
    }
  };

  const handleDownloadSampleCSV = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(SAMPLE_CSV_ROWS);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sample_tokens_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      playTickSound(880, 0.05);
    } catch (e) {
      console.error("CSV template download error:", e);
      alert("Failed to create CSV template: " + e.message);
    }
  };

  // --- 2. Export Current Tokens Database ---
  const handleExportDatabaseExcel = (exportMode = 'all') => {
    try {
      const listToExport = exportMode === 'available' ? availableTokens : validTokens;
      if (listToExport.length === 0) {
        return alert(`No ${exportMode === 'available' ? 'available' : ''} tokens found to export.`);
      }

      const rows = listToExport.map((token, idx) => ({
        "Serial / Token ID": token,
        "Status": usedTokens.includes(token) ? "Redeemed / Used" : "Available / Unused",
        "Entry Order": idx + 1
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      const sheetName = exportMode === 'available' ? "Available Tokens" : "All Event Tokens";
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, `event_tokens_${exportMode}_${new Date().toISOString().split('T')[0]}.xlsx`);
      playTickSound(880, 0.05);
    } catch (e) {
      console.error("Export error:", e);
      alert("Failed to export Excel file: " + e.message);
    }
  };

  const handleExportDatabaseCSV = (exportMode = 'all') => {
    try {
      const listToExport = exportMode === 'available' ? availableTokens : validTokens;
      if (listToExport.length === 0) {
        return alert(`No ${exportMode === 'available' ? 'available' : ''} tokens found to export.`);
      }

      const headers = ['Token Number', 'Status'];
      const rows = listToExport.map(t => 
        `"${t}","${usedTokens.includes(t) ? 'Redeemed' : 'Available'}"`
      );
      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `event_tokens_${exportMode}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      playTickSound(880, 0.05);
    } catch (e) {
      console.error("Export error:", e);
      alert("Failed to export CSV: " + e.message);
    }
  };

  // --- 3. Parse Uploaded File (.xlsx, .xls, .csv, .txt) ---
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isTextOrCsv = fileName.endsWith('.csv') || fileName.endsWith('.txt');

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        let extractedTokens = [];

        if (isTextOrCsv) {
          const content = evt.target.result;
          // Split by line breaks or commas
          const lines = content.split(/[\r\n]+/);
          lines.forEach(line => {
            const parts = line.split(',');
            parts.forEach(p => {
              const clean = p.replace(/["']/g, '').trim();
              if (clean && !clean.toLowerCase().includes('token') && !clean.toLowerCase().includes('serial') && !clean.toLowerCase().includes('status')) {
                extractedTokens.push(clean);
              }
            });
          });
        } else {
          // Excel spreadsheet
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: '', header: 1 });

          if (!rawJson || rawJson.length === 0) {
            setUploadStatus({
              type: 'error',
              message: 'The uploaded spreadsheet appears empty.'
            });
            return;
          }

          // Determine which column has the token
          let tokenColIdx = 0;
          const headerRow = rawJson[0] || [];
          const candidateHeaders = ['token', 'token number', 'token_number', 'serial', 'serial number', 'serial_number', 'code', 'ticket', 'id', 'pass', 'number'];
          
          headerRow.forEach((cell, idx) => {
            const str = String(cell).toLowerCase().trim();
            if (candidateHeaders.some(cand => str.includes(cand))) {
              tokenColIdx = idx;
            }
          });

          // Skip header row if it contains text headers
          const startIndex = typeof headerRow[tokenColIdx] === 'string' && 
            candidateHeaders.some(cand => String(headerRow[tokenColIdx]).toLowerCase().includes(cand)) ? 1 : 0;

          for (let r = startIndex; r < rawJson.length; r++) {
            const row = rawJson[r];
            if (row && row[tokenColIdx] !== undefined) {
              const val = String(row[tokenColIdx]).trim();
              if (val) extractedTokens.push(val);
            }
          }
        }

        // Deduplicate while preserving case
        const uniqueTokens = [...new Set(extractedTokens)];

        if (uniqueTokens.length === 0) {
          setUploadStatus({
            type: 'error',
            message: 'No valid token numbers found in the file. Please check column headers or use our sample template.'
          });
          setParsedPreview(null);
          return;
        }

        setParsedPreview({
          tokens: uniqueTokens,
          totalFound: extractedTokens.length,
          uniqueCount: uniqueTokens.length,
          duplicatesCount: extractedTokens.length - uniqueTokens.length,
          fileName: file.name
        });

        setUploadStatus({
          type: 'success',
          message: `Ready to import: Found ${uniqueTokens.length} unique tokens in "${file.name}". Click "Confirm Import" below.`
        });
        playTickSound(750, 0.05);

      } catch (err) {
        console.error("File parsing error:", err);
        setUploadStatus({
          type: 'error',
          message: 'Error parsing file: ' + err.message
        });
      }
    };

    if (isTextOrCsv) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  // --- 4. Confirm Import ---
  const handleConfirmImport = () => {
    if (!parsedPreview || !parsedPreview.tokens?.length) return;

    let updatedList = [];
    let updatedUsed = [...usedTokens];

    if (uploadMode === 'replace') {
      updatedList = parsedPreview.tokens;
      updatedUsed = []; // reset used for new list
    } else {
      // Append / Merge
      updatedList = [...new Set([...validTokens, ...parsedPreview.tokens])];
    }

    onUpdateSettings('validSerialNumbers', updatedList);
    onUpdateSettings('usedSerialNumbers', updatedUsed);

    setRawTextValue(updatedList.join('\n'));
    setUploadStatus({
      type: 'success',
      message: `🎉 Successfully ${uploadMode === 'replace' ? 'replaced database with' : 'added'} ${parsedPreview.tokens.length} tokens!`
    });
    setParsedPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    playCorrectChime();
  };

  // --- 5. Generate Batch Serials ---
  const handleGenerateSerials = () => {
    if (genCount < 1) return alert("Count must be at least 1");
    const generated = [];

    for (let i = 0; i < genCount; i++) {
      let code = '';
      if (genStyle === 'alphanumeric') {
        const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
        let randomPart = '';
        for (let c = 0; c < 4; c++) {
          randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        code = `${genPrefix.trim()}${randomPart}`;
      } else {
        const num = genStartIndex + i;
        const padded = num.toString().padStart(genDigits, '0');
        code = `${genPrefix.trim()}${padded}`;
      }
      generated.push(code);
    }

    const merged = [...new Set([...validTokens, ...generated])];
    onUpdateSettings('validSerialNumbers', merged);
    setRawTextValue(merged.join('\n'));
    playCorrectChime();
    alert(`Successfully generated and added ${genCount} tokens to the database!`);
  };

  // --- 6. Quick Single Token Add ---
  const handleAddSingleToken = (e) => {
    e?.preventDefault();
    const clean = singleToken.trim().toUpperCase();
    if (!clean) return;

    if (validTokens.includes(clean)) {
      alert(`Token "${clean}" already exists in the authorized database.`);
      return;
    }

    const merged = [...validTokens, clean];
    onUpdateSettings('validSerialNumbers', merged);
    setRawTextValue(merged.join('\n'));
    setSingleToken('');
    playTickSound(880, 0.05);
  };

  // --- 7. Toggle Used / Remove Token ---
  const handleToggleUsed = (token) => {
    let newUsed = [...usedTokens];
    if (newUsed.includes(token)) {
      newUsed = newUsed.filter(t => t !== token);
    } else {
      newUsed.push(token);
    }
    onUpdateSettings('usedSerialNumbers', newUsed);
    playTickSound(700, 0.04);
  };

  const handleDeleteToken = (token) => {
    if (window.confirm(`Delete token "${token}" from the database?`)) {
      const newValid = validTokens.filter(t => t !== token);
      const newUsed = usedTokens.filter(t => t !== token);
      onUpdateSettings('validSerialNumbers', newValid);
      onUpdateSettings('usedSerialNumbers', newUsed);
      setRawTextValue(newValid.join('\n'));
    }
  };

  const handleResetAllUsed = () => {
    if (window.confirm("Reset all redeemed tokens? This will mark all tokens as Available again while keeping the list intact.")) {
      onUpdateSettings('usedSerialNumbers', []);
      playTickSound(800, 0.05);
    }
  };

  const handleClearDatabase = () => {
    if (window.confirm("Are you sure you want to clear ALL authorized tokens? Participants will not be able to play until new tokens are added.")) {
      onUpdateSettings('validSerialNumbers', []);
      onUpdateSettings('usedSerialNumbers', []);
      setRawTextValue('');
    }
  };

  const handleCopyToken = (token) => {
    navigator.clipboard?.writeText(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
    playTickSound(900, 0.04);
  };

  // Filter and search tokens for table
  const displayedTokens = validTokens.filter(token => {
    const matchesSearch = token.toLowerCase().includes(searchTerm.toLowerCase());
    const isUsed = usedTokens.includes(token);
    if (!matchesSearch) return false;
    if (statusFilter === 'available') return !isUsed;
    if (statusFilter === 'used') return isUsed;
    return true;
  });

  return (
    <div className="space-y-6">
      
      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider">Total Tokens</span>
            <SafeIcon icon={FiList} className="text-indigo-500" />
          </div>
          <div className="text-2xl font-black text-slate-800 dark:text-white font-mono">
            {validTokens.length}
          </div>
          <span className="text-[10px] text-slate-400 mt-1">Configured in database</span>
        </div>

        <div className="p-4 rounded-xl bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40 flex flex-col justify-between">
          <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider">Available</span>
            <SafeIcon icon={FiCheckCircle} className="text-emerald-500" />
          </div>
          <div className="text-2xl font-black text-emerald-700 dark:text-emerald-300 font-mono">
            {availableTokens.length}
          </div>
          <span className="text-[10px] text-emerald-600/70 dark:text-emerald-400/70 mt-1">Ready for visitors</span>
        </div>

        <div className="p-4 rounded-xl bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 flex flex-col justify-between">
          <div className="flex items-center justify-between text-amber-600 dark:text-amber-400 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider">Redeemed</span>
            <SafeIcon icon={FiClock} className="text-amber-500" />
          </div>
          <div className="text-2xl font-black text-amber-700 dark:text-amber-300 font-mono">
            {redeemedTokens.length}
          </div>
          <span className="text-[10px] text-amber-600/70 dark:text-amber-400/70 mt-1">Already played</span>
        </div>

        <div className="p-4 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800/40 flex flex-col justify-between">
          <div className="flex items-center justify-between text-indigo-600 dark:text-indigo-400 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider">Gate Status</span>
            <SafeIcon icon={FiShield} className="text-indigo-500" />
          </div>
          <div className="text-base font-black text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5 mt-1">
            {settings?.requireSerialNumber ? (
              <span className="px-2 py-0.5 rounded-full bg-indigo-500 text-white text-xs">ENFORCED</span>
            ) : (
              <span className="px-2 py-0.5 rounded-full bg-slate-300 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs">OPEN ACCESS</span>
            )}
          </div>
          <span className="text-[10px] text-slate-400 mt-1">
            {settings?.requireSerialNumber ? 'Tokens required' : 'Anyone can play'}
          </span>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-sm space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-3">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <button
              type="button"
              onClick={() => setActiveSubTab('upload')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeSubTab === 'upload'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <SafeIcon icon={FiUpload} className="text-sm" />
              <span>Upload Excel / CSV</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveSubTab('generate')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeSubTab === 'generate'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <SafeIcon icon={FiKey} className="text-sm" />
              <span>Batch Generate Serials</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveSubTab('quick_add')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeSubTab === 'quick_add'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <SafeIcon icon={FiPlus} className="text-sm" />
              <span>Add Single Token</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveSubTab('paste')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeSubTab === 'paste'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <SafeIcon icon={FiFileText} className="text-sm" />
              <span>Raw Textarea</span>
            </button>
          </div>

          {/* Quick Template Download Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadSampleExcel}
              className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 border border-emerald-200 dark:border-emerald-800/50 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
              title="Download sample Excel template"
            >
              <SafeIcon icon={FiDownload} className="text-xs" />
              <span>Sample .xlsx</span>
            </button>
            <button
              type="button"
              onClick={handleDownloadSampleCSV}
              className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
              title="Download sample CSV template"
            >
              <SafeIcon icon={FiDownload} className="text-xs" />
              <span>Sample .csv</span>
            </button>
          </div>
        </div>

        {/* SUBTAB 1: UPLOAD EXCEL / CSV */}
        {activeSubTab === 'upload' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 rounded-xl p-4">
              <div>
                <h4 className="text-sm font-bold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
                  <SafeIcon icon={FiUpload} className="text-indigo-600 dark:text-indigo-400" />
                  Import Custom Tokens from Excel or CSV
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Supports <code>.xlsx</code>, <code>.xls</code>, <code>.csv</code>, and <code>.txt</code> files with token/serial codes.
                </p>
              </div>

              {/* Import Mode Selector */}
              <div className="flex items-center bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-800 rounded-lg p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setUploadMode('append')}
                  className={`px-3 py-1 rounded font-bold transition-all ${
                    uploadMode === 'append'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-indigo-600'
                  }`}
                >
                  Append (Add to Existing)
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('replace')}
                  className={`px-3 py-1 rounded font-bold transition-all ${
                    uploadMode === 'replace'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-indigo-600'
                  }`}
                >
                  Replace All
                </button>
              </div>
            </div>

            {/* Drop / Pick Zone */}
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-indigo-200 dark:border-indigo-800/60 hover:border-indigo-400 dark:hover:border-indigo-600 rounded-2xl p-6 text-center cursor-pointer bg-slate-50/50 dark:bg-slate-900/50 transition-colors group"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx, .xls, .csv, .txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div className="w-12 h-12 mx-auto rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center text-xl group-hover:scale-110 transition-transform mb-3">
                <SafeIcon icon={FiUpload} />
              </div>
              <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                Click or drag & drop your Excel or CSV file here
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Column named <strong>Token</strong>, <strong>Serial</strong>, or the very first column will be extracted automatically.
              </p>
            </div>

            {/* Status Alert */}
            {uploadStatus && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-3.5 rounded-xl text-xs font-medium flex items-center gap-2.5 ${
                  uploadStatus.type === 'success'
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                    : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                }`}
              >
                <SafeIcon icon={uploadStatus.type === 'success' ? FiCheckCircle : FiAlertCircle} className="text-base shrink-0" />
                <span>{uploadStatus.message}</span>
              </motion.div>
            )}

            {/* Parsed Preview Card */}
            {parsedPreview && (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-slate-50 dark:bg-slate-800/60 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4 space-y-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <SafeIcon icon={FiFileText} className="text-indigo-500" />
                    Preview: {parsedPreview.fileName}
                  </span>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 font-bold">
                      {parsedPreview.uniqueCount} Unique Tokens
                    </span>
                    {parsedPreview.duplicatesCount > 0 && (
                      <span className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 text-[10px]">
                        {parsedPreview.duplicatesCount} Duplicates Skipped
                      </span>
                    )}
                  </div>
                </div>

                {/* Sample items pill cloud */}
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase">First Sample Tokens:</span>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pt-1">
                    {parsedPreview.tokens.slice(0, 15).map((tok, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 font-mono text-xs text-slate-700 dark:text-slate-200">
                        {tok}
                      </span>
                    ))}
                    {parsedPreview.tokens.length > 15 && (
                      <span className="text-xs text-slate-400 self-center pl-1 font-medium">
                        +{parsedPreview.tokens.length - 15} more...
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => {
                      setParsedPreview(null);
                      setUploadStatus(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="px-4 py-2 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmImport}
                    className="px-5 py-2 rounded-lg text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md transition-all flex items-center gap-1.5"
                  >
                    <SafeIcon icon={FiCheckCircle} />
                    <span>Confirm Import ({parsedPreview.uniqueCount} Tokens)</span>
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* SUBTAB 2: BATCH GENERATE SERIALS */}
        {activeSubTab === 'generate' && (
          <div className="bg-indigo-50/40 dark:bg-indigo-950/10 border border-indigo-100 dark:border-indigo-900/30 rounded-xl p-5 space-y-4">
            <div>
              <h4 className="text-sm font-bold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
                <SafeIcon icon={FiKey} className="text-indigo-600" />
                Automatic Batch Serial Number Generator
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Generate sequential or secure alphanumeric serial tokens in bulk and merge directly into the database.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">Prefix</label>
                <input
                  type="text"
                  value={genPrefix}
                  onChange={e => setGenPrefix(e.target.value)}
                  placeholder="e.g. EVENT-"
                  className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">Starting Number</label>
                <input
                  type="number"
                  min="1"
                  value={genStartIndex}
                  onChange={e => setGenStartIndex(Number(e.target.value))}
                  disabled={genStyle === 'alphanumeric'}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">Zero Padding</label>
                <select
                  value={genDigits}
                  onChange={e => setGenDigits(Number(e.target.value))}
                  disabled={genStyle === 'alphanumeric'}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                >
                  <option value={3}>3 Digits (001)</option>
                  <option value={4}>4 Digits (0001)</option>
                  <option value={5}>5 Digits (00001)</option>
                  <option value={6}>6 Digits (000001)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  max="5000"
                  value={genCount}
                  onChange={e => setGenCount(Number(e.target.value))}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">Pattern Style</label>
                <select
                  value={genStyle}
                  onChange={e => setGenStyle(e.target.value)}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="sequential">Sequential (0001, 0002...)</option>
                  <option value="alphanumeric">Alphanumeric (7X9B, 4K2M...)</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                Sample Output: <strong className="text-indigo-600 dark:text-indigo-400">
                  {genStyle === 'alphanumeric' ? `${genPrefix}8B2K` : `${genPrefix}${genStartIndex.toString().padStart(genDigits, '0')}`}
                </strong> to <strong className="text-indigo-600 dark:text-indigo-400">
                  {genStyle === 'alphanumeric' ? `${genPrefix}9X7P` : `${genPrefix}${(genStartIndex + genCount - 1).toString().padStart(genDigits, '0')}`}
                </strong>
              </div>

              <button
                type="button"
                onClick={handleGenerateSerials}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer"
              >
                <SafeIcon icon={FiPlus} />
                <span>Generate & Add {genCount} Serials</span>
              </button>
            </div>
          </div>
        )}

        {/* SUBTAB 3: QUICK ADD SINGLE TOKEN */}
        {activeSubTab === 'quick_add' && (
          <form onSubmit={handleAddSingleToken} className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 rounded-xl p-5 space-y-3">
            <div>
              <h4 className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <SafeIcon icon={FiPlus} className="text-indigo-500" />
                Add Single Custom Token / Visitor Code
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Convenient for walk-in VIPs, manual tickets, or individual badge numbers.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={singleToken}
                onChange={e => setSingleToken(e.target.value)}
                placeholder="e.g. VIP-BOOTH-01 or TICKET-9921"
                className="flex-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm uppercase font-mono text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="submit"
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiPlus} />
                <span>Add Token</span>
              </button>
            </div>
          </form>
        )}

        {/* SUBTAB 4: RAW TEXTAREA */}
        {activeSubTab === 'paste' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-slate-800 dark:text-white">Raw Token Textarea</h4>
                <p className="text-xs text-slate-500">Edit or paste multiple tokens directly (one per line).</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const parsed = rawTextValue.split('\n').map(s => s.trim()).filter(Boolean);
                  onUpdateSettings('validSerialNumbers', [...new Set(parsed)]);
                  playCorrectChime();
                  alert(`Updated authorized database with ${parsed.length} tokens!`);
                }}
                className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Save Raw List
              </button>
            </div>

            <textarea
              rows={7}
              value={rawTextValue}
              onChange={e => setRawTextValue(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl p-3 font-mono text-xs text-slate-800 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 whitespace-pre"
              placeholder="Paste tokens here, one per line..."
            />
          </div>
        )}
      </div>

      {/* Database Viewer, Search & Live Status Table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm space-y-4">
        
        {/* Table Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <SafeIcon icon={FiList} className="text-indigo-500" />
              Authorized Token Directory
            </h3>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
              {displayedTokens.length} Shown / {validTokens.length} Total
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Search Box */}
            <div className="relative min-w-[200px]">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search token..."
                className="w-full pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Filter buttons */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`px-2.5 py-1 rounded font-bold transition-all ${
                  statusFilter === 'all'
                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('available')}
                className={`px-2.5 py-1 rounded font-bold transition-all ${
                  statusFilter === 'available'
                    ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                Available ({availableTokens.length})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('used')}
                className={`px-2.5 py-1 rounded font-bold transition-all ${
                  statusFilter === 'used'
                    ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                Used ({redeemedTokens.length})
              </button>
            </div>

            {/* Export Dropdown */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleExportDatabaseExcel('all')}
                className="px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/60 text-xs font-bold hover:bg-emerald-100 transition-colors flex items-center gap-1"
                title="Export all tokens to Excel"
              >
                <SafeIcon icon={FiDownload} />
                <span>Export .xlsx</span>
              </button>

              <button
                type="button"
                onClick={() => handleExportDatabaseCSV('available')}
                className="px-2.5 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800/60 text-xs font-bold hover:bg-indigo-100 transition-colors flex items-center gap-1"
                title="Export only unused tokens to CSV for printing"
              >
                <SafeIcon icon={FiDownload} />
                <span>Unused .csv</span>
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Tokens Table */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
          {displayedTokens.length === 0 ? (
            <div className="p-8 text-center text-slate-400 space-y-2">
              <SafeIcon icon={FiKey} className="text-3xl mx-auto opacity-40" />
              <p className="text-sm font-medium">No tokens found matching your criteria.</p>
              <p className="text-xs">Upload an Excel/CSV file or click "Batch Generate Serials" to get started.</p>
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 font-bold uppercase sticky top-0 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-2.5">#</th>
                  <th className="px-4 py-2.5">Token / Serial ID</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                {displayedTokens.map((token, index) => {
                  const isUsed = usedTokens.includes(token);
                  return (
                    <tr key={token} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-4 py-2.5 text-slate-400 font-sans">{index + 1}</td>
                      <td className="px-4 py-2.5 font-bold text-slate-800 dark:text-slate-200">
                        <div className="flex items-center gap-2">
                          <span>{token}</span>
                          <button
                            type="button"
                            onClick={() => handleCopyToken(token)}
                            className="text-slate-400 hover:text-indigo-500 transition-colors"
                            title="Copy to clipboard"
                          >
                            <SafeIcon icon={copiedToken === token ? FiCheck : FiCopy} className="text-xs" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 font-sans">
                        {isUsed ? (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-bold">
                            REDEEMED
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold">
                            AVAILABLE
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-sans space-x-1.5">
                        <button
                          type="button"
                          onClick={() => handleToggleUsed(token)}
                          className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${
                            isUsed
                              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 hover:bg-emerald-100'
                              : 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 hover:bg-amber-100'
                          }`}
                          title={isUsed ? "Mark as Available" : "Mark as Used"}
                        >
                          {isUsed ? 'Mark Available' : 'Mark Used'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteToken(token)}
                          className="px-2 py-1 rounded text-[11px] font-semibold bg-red-50 dark:bg-red-950/40 text-red-500 hover:bg-red-100 transition-colors"
                          title="Delete token"
                        >
                          <SafeIcon icon={FiTrash2} className="inline text-xs" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Bottom Mass Action Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
          <div className="text-xs text-slate-500">
            {redeemedTokens.length > 0 ? (
              <span>{redeemedTokens.length} tokens have already been redeemed by event attendees.</span>
            ) : (
              <span>All {validTokens.length} tokens are currently available for play.</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleResetAllUsed}
              disabled={redeemedTokens.length === 0}
              className="text-xs font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 border border-amber-200 dark:border-amber-800/40 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
            >
              Reset Redeemed Tokens ({redeemedTokens.length})
            </button>
            <button
              type="button"
              onClick={handleClearDatabase}
              disabled={validTokens.length === 0}
              className="text-xs font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 border border-red-200 dark:border-red-800/40 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
            >
              Clear Entire Database
            </button>
          </div>
        </div>

      </div>

    </div>
  );
}
